import { type FC, useMemo } from "react";

import type { UsageBreakdowns as UsageBreakdownsType } from "../../types/usage";

import { formatTokenCount, formatUsd } from "../../utils/currency";
import { DataTable } from "../common";
import { Badge } from "../common/Badge";
import { PanelSection } from "../common/PanelSection";
import { SectionHeader } from "../common/SectionHeader";
import { Text } from "../common/Text";
import { DonutChart, getChartColor } from "./DonutChart";

const EMPTY = "—";

interface BreakdownRowData {
  key: string;
  label: string;
  tokens: number;
  cost: number | undefined;
  color: string;
}

interface BreakdownSectionProps {
  title: string;
  icon: React.ReactNode;
  rows: BreakdownRowData[];
  metric: "cost" | "tokens";
}

const BREAKDOWN_COLUMN_LABELS = ["Tokens", "Cost", "%"] as const;

const BreakdownTableRow: FC<{ percentage: number; row: BreakdownRowData }> = ({
  percentage,
  row,
}) => (
  <DataTable.Row>
    <DataTable.Cell>
      <div className="flex items-center gap-2">
        <div
          className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
          style={{ backgroundColor: row.color }}
        />
        <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
          {row.label}
        </span>
      </div>
    </DataTable.Cell>
    <DataTable.Cell className="text-right font-mono">
      {formatTokenCount(row.tokens)}
    </DataTable.Cell>
    <DataTable.Cell className="text-right font-mono">
      {formatUsd(row.cost)}
    </DataTable.Cell>
    <DataTable.Cell className="text-right font-mono text-gray-500 dark:text-white/60">
      {percentage.toFixed(1)}%
    </DataTable.Cell>
  </DataTable.Row>
);

const BreakdownTable: FC<{
  rows: BreakdownRowData[];
  title: string;
  total: number;
  metric: "cost" | "tokens";
}> = ({ rows, title, total, metric }) => (
  <DataTable className="text-xs">
    <DataTable.Header>
      <tr>
        <DataTable.HeaderCell>{title}</DataTable.HeaderCell>
        {BREAKDOWN_COLUMN_LABELS.map((label) => (
          <DataTable.HeaderCell key={label} className="text-right">
            {label}
          </DataTable.HeaderCell>
        ))}
      </tr>
    </DataTable.Header>
    <DataTable.Body>
      {rows.map((row) => {
        const value = metric === "cost" ? (row.cost ?? 0) : row.tokens;
        const percentage = total > 0 ? (value / total) * 100 : 0;
        return (
          <BreakdownTableRow key={row.key} percentage={percentage} row={row} />
        );
      })}
    </DataTable.Body>
  </DataTable>
);

const BreakdownSection: FC<BreakdownSectionProps> = ({
  title,
  icon,
  rows,
  metric,
}) => {
  const chartData = useMemo(
    () =>
      rows.map((row) => ({
        color: row.color,
        label: row.label,
        value: metric === "cost" ? (row.cost ?? 0) : row.tokens,
      })),
    [rows, metric],
  );

  const total = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData],
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <PanelSection
      title={title}
      headerContent={
        <div className="flex items-center gap-2">
          <span className="text-gray-400 dark:text-white/40">{icon}</span>
          <Text variant="label" as="p">
            {title}
          </Text>
        </div>
      }
      className="h-full"
      bodyClassName="!p-0"
    >
      <div className="flex flex-col gap-3 p-3 lg:flex-row lg:items-start">
        {/* Donut Chart */}
        <div className="flex justify-center lg:flex-shrink-0">
          <DonutChart
            data={chartData}
            size={140}
            strokeWidth={20}
            showLegend={false}
            centerLabel={
              <div className="flex flex-col items-center">
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {metric === "cost"
                    ? formatUsd(total)
                    : formatTokenCount(total)}
                </span>
                <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-white/50">
                  {metric === "cost" ? "Total" : "Tokens"}
                </span>
              </div>
            }
          />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-hidden">
          <div className="custom-scrollbar max-h-72 overflow-auto">
            <BreakdownTable
              rows={rows}
              title={title}
              total={total}
              metric={metric}
            />
          </div>
        </div>
      </div>
    </PanelSection>
  );
};

export interface UsageBreakdownChartsProps {
  breakdowns: UsageBreakdownsType | null;
}

export const UsageBreakdownCharts: FC<UsageBreakdownChartsProps> = ({
  breakdowns,
}) => {
  if (!breakdowns) {
    return null;
  }

  const providerRows: BreakdownRowData[] = breakdowns.byProvider.map(
    (row, idx) => ({
      color: getChartColor(idx),
      cost: row.totals.costUsd,
      key: row.provider,
      label: row.provider,
      tokens: row.totals.totalTokens,
    }),
  );

  const modelRows: BreakdownRowData[] = breakdowns.byModel.map((row, idx) => ({
    color: getChartColor(idx),
    cost: row.totals.costUsd,
    key: row.model ?? EMPTY,
    label: row.model ?? EMPTY,
    tokens: row.totals.totalTokens,
  }));

  const stageRows: BreakdownRowData[] = breakdowns.byStage.map((row, idx) => ({
    color: getChartColor(idx),
    cost: row.totals.costUsd,
    key: row.stage,
    label: row.stage,
    tokens: row.totals.totalTokens,
  }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionHeader title="Breakdowns" />
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="default" size="md">
            Entries: {breakdowns.total.toLocaleString()}
          </Badge>
          {breakdowns.partial && (
            <Badge variant="warning" size="md">
              Partial
            </Badge>
          )}
        </div>
      </div>

      <div className="grid gap-3 xl:grid-cols-2">
        <BreakdownSection
          title="By Provider"
          icon={
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          }
          rows={providerRows}
          metric="cost"
        />

        <BreakdownSection
          title="By Model"
          icon={
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          }
          rows={modelRows}
          metric="cost"
        />

        <BreakdownSection
          title="By Stage"
          icon={
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
              />
            </svg>
          }
          rows={stageRows}
          metric="cost"
        />
      </div>
    </div>
  );
};
